import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from "react-router";
import logoReverse from "../assets/logo/giraffe-logo-reverse.svg";
import { problemMessage } from "../shared/api/problem";
import { useNeedsAttention, useProjects, useUserDirectory } from "../shared/api/queries";
import { createHrefFor, isSprintBoardPath } from "../features/boards/createOrigin";
import { MentionDirectoryContext, type MentionDirectory } from "../shared/components/mention/MentionDirectoryContext";
import { mentionDirectoryOf } from "../shared/components/mention/mentionDirectory";
import { PersonAvatar } from "../shared/components/PersonAvatar";
import { userLabel } from "../shared/lib/personLabel";
import { Popover } from "../shared/components/Popover";
import { LocaleContext } from "../shared/lib/locale-context";
import { setShortcutsEnabled, shortcutsEnabled, useShortcuts } from "../shared/lib/shortcuts";
import { readLocal, writeLocal } from "../shared/lib/storage";
import { useActiveProjectHint } from "../shared/lib/activeProject";
import { usePageHistoryScroll } from "../shared/lib/historyScroll";
import { MentionEmailToggle } from "../features/mentions/MentionEmailToggle";
import { GuidesMenu } from "../features/guides/GuidesMenu";
import { GuidesProvider } from "../features/guides/GuidesProvider";
import { WhatsNewProvider } from "../features/whatsNew/WhatsNewProvider";
import { useWhatsNew } from "../features/whatsNew/whatsNew-context";
import { isPartnerAudience } from "../features/whatsNew/model";
import { useJumpToRef } from "../features/search/useJumpToRef";
import { attentionCount, flagLines, logTimeWords } from "./flags";
import { navGroupsFor, screenHref } from "./screens";
import { logout } from "./session/api";
import { useSession, type Session } from "../shared/session/session-context";
import { mayCreateAnywhere, mayCreateSomethingIn, mayOpenApprovals, mayOpenDevOpsQueue, mayOpenStatusReports } from "../shared/session/projectAccess";
import { mayOpenEmergencyReport } from "../features/emergency/emergencyReportModel";
import { TestModeControl, TestModeStrip } from "./TestMode";
import { FeedbackButton, FeedbackLayer } from "../features/feedback/PinFeedback";
import { FEEDBACK_TITLE, useFeedbackOffer } from "../features/feedback/offer";
import { PHONE_QUERY, useMediaQuery } from "../shared/lib/useMediaQuery";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { ProjectPicker } from "../shared/components/ProjectPicker";
import { PhoneSearchBar, PhoneSearchButton } from "./PhoneSearch";
import { usePhoneSearch } from "./phoneSearchState";

function WhatsOnToday({ session, bellOn }: { session: Session; bellOn: boolean }) {
  const [enabled, setEnabled] = useState(shortcutsEnabled);
  return (
    <div className="stack gf-whatson">
      <h2 className="gf-pop-title">What&rsquo;s on today</h2>
      <ul className="gf-flaglist">
        {flagLines(session.flags).map((line) => (
          <li key={line.name}>
            <span>{line.label}</span>
            <b className={line.on ? "on" : "off"}>{line.on ? "On" : "Off"}</b>
          </li>
        ))}
      </ul>
      <p className="help">{logTimeWords(session.modes.logTime)}</p>
      <label className="gf-check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            setShortcutsEnabled(event.target.checked);
            setEnabled(event.target.checked);
          }}
        />
        Single-key keyboard shortcuts (/ search, c create, {bellOn ? "n notifications, " : ""}y copy key, Shift+Y copy ID)
      </label>
    </div>
  );
}

/** The "new" dot of What's new (ruling of 17 Oct 2026); the words are for screen readers. */
function NewDot() {
  return (
    <span className="gf-new-dot" role="img" aria-label="new" />
  );
}

/** PMO Console look: 56px navy header with the reverse logo, 250px white side nav, content on the ground colour. */
export function AppShell() {
  const session = useSession();
  return (
    <LocaleContext value={session.locale}>
      {/* What's new (ruling of 17 Oct 2026) sits outside the guided tours so they can hold back while its dialog is due. */}
      <WhatsNewProvider>
        <ShellBody />
      </WhatsNewProvider>
    </LocaleContext>
  );
}

function ShellBody() {
  const session = useSession();
  const whatsNew = useWhatsNew();
  const partnerAudience = isPartnerAudience(session);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projects = useProjects();
  // GRF-16: Back and Forward return each page to where it was scrolled; a newly opened page starts at its top.
  usePageHistoryScroll();
  const projectMatch = useMatch("/p/:projectKey/*");
  const urlProject = projectMatch?.params.projectKey;
  const hintedProject = useActiveProjectHint();
  const lastProject = readLocal<string>("lastProject");
  const known = projects.data ?? [];
  // Sidebar nav needs *some* project once we leave `/p/:projectKey/*` (UX finding 7, 2026-09-17 review), so it
  // never collapses to empty Plan/Operate/Register groups with no way back to where the user came from:
  //  1. the URL's own :projectKey, when the route carries one;
  //  2. a project a page reported it belongs to (e.g. an item detail page reporting the item's project);
  //  3. the last project we saw, IF the project list loaded and still contains it (a real, still-existing
  //     project) — OR, when the project list hasn't loaded (it 404s outright until the projects endpoint
  //     ships), trust it anyway: an unvalidated recent project beats a nav with nothing in it, and once the
  //     list does load a stale/deleted key stops qualifying automatically;
  //  4. the first known project, so a fresh session with a working project list still shows *a* project's nav.
  const projectListLoaded = projects.isSuccess;
  // A person who holds no project (e.g. test mode viewing as someone with no role anywhere): no project-scoped entry is
  // offered at all, whatever the URL, a page's hint or the remembered project says (every such page would be "not found").
  const noProjects = projectListLoaded && known.length === 0;
  const projectKey = noProjects
    ? ""
    : urlProject ??
    hintedProject ??
    (lastProject && (!projectListLoaded || known.some((p) => p.key === lastProject)) ? lastProject : undefined) ??
    known[0]?.key ??
    "";
  // R10-UX1: Create only where the server lets this person create (a Viewer, or an admin in role test mode as Viewer,
  // does not). On a project's own pages that project decides; elsewhere any project they may create in, and the link
  // presets the remembered project only when they may create there.
  const contextProject = urlProject ?? hintedProject;
  const showCreate = contextProject ? mayCreateSomethingIn(session, contextProject) : mayCreateAnywhere(session);
  // Batch 17 R2 (GRF-41): from the project's sprint board, Create (and `c`) preselect its active sprint (sprint=active).
  const fromSprintBoard =
    !!urlProject && urlProject === projectKey && isSprintBoardPath(location.pathname, urlProject, session.flag("Sprints"), known.find((p) => p.key === urlProject)?.sprintsEnabled);
  const createHref = projectKey && mayCreateSomethingIn(session, projectKey) ? createHrefFor(projectKey, fromSprintBoard) : "/items/new";
  const searchRef = useRef<HTMLInputElement>(null);
  const jump = useJumpToRef();
  // SA-04 §6: the count is fetched for partners too — the server computes mentions_unread for them, and only that. With the bell
  // on, the bell reads the same query (and polls it); this observer only feeds the pill / Mentions link shown without the bell.
  const attention = useNeedsAttention(true);
  const count = attentionCount(attention.data, session);
  const mentionsUnread = attention.data?.items.find((item) => item.kind === "mentions_unread")?.count ?? 0;
  const mentionsOn = session.flag("Mentions");
  // Batch 17 (notifications panel §1.1, ruling P6): the bell for everyone signed in as a person (internal and partner), not for
  // an AI agent's account. For a partner it replaces the Mentions link.
  const bellOn = session.flag("NotificationPanel") && session.user.isAgent !== true;
  const [signOutError, setSignOutError] = useState<string | null>(null);
  // D-B17-03: below 760 px the header keeps only Create, the bell and the account button, so it fits a 320 px phone for every
  // role. The "View as" test-mode switch, Feedback and the partner chip move into the account menu (a bottom sheet there).
  // Rendered in exactly one place at a time, so there is never a second "View as" control or a hidden focus stop.
  const phone = useMediaQuery(PHONE_QUERY);
  // Batch 18 §11.3 (Features__PhoneSearch): on a phone the search is an icon that opens a full-width bar over the header.
  const phoneSearchOn = phone && session.flag("PhoneSearch");
  const phoneSearch = usePhoneSearch(phoneSearchOn);
  const feedbackOffer = useFeedbackOffer();
  const [phoneFeedbackOpen, setPhoneFeedbackOpen] = useState(false);
  // SA-04 §5: one directory for every mention chip under the shell — live names by the shared person-label rule (the
  // same English label as assignee and author lines, in every locale; GRF-61), deactivated people muted, anyone outside
  // the viewer's directory shown by the label snapshot.
  const users = useUserDirectory();
  const mentionDirectory = useMemo<MentionDirectory>(() => mentionDirectoryOf(users.byId, String(session.user.id)), [users.byId, session.user.id]);

  useEffect(() => {
    if (urlProject) writeLocal("lastProject", urlProject);
  }, [urlProject]);

  useShortcuts({
    "/": () => (phoneSearchOn ? phoneSearch.openBar() : searchRef.current?.focus()),
    c: () => {
      if (showCreate) void navigate(createHref);
    },
  });

  function searchFor(text: string) {
    void jump(text, contextProject);
  }

  function onSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("q");
    void jump(typeof value === "string" ? value : "", contextProject);
  }

  function onProjectChange(next: string) {
    if (urlProject) {
      void navigate(location.pathname.replace(/^\/p\/[^/]+/, `/p/${encodeURIComponent(next)}`));
    } else {
      void navigate(`/p/${encodeURIComponent(next)}`);
    }
  }

  async function onSignOut() {
    try {
      await logout();
      queryClient.clear();
      await navigate("/login", { replace: true });
    } catch (error) {
      setSignOutError(problemMessage(error));
    }
  }

  const groups = navGroupsFor({
    isPartner: session.isPartner,
    // Settings ruling (2 Oct 2026): the Settings group follows the test view, so it is hidden in test mode.
    canOpenSettings: session.canOpenSettings,
    flag: (name) => session.flag(name),
    // O-R12-1: entries whose page would refuse this caller are not offered (CM report, DevOps queue). Batch 20 B20-1: and the
    // project entries whose menu.* code the caller does not hold on the nav's project (Settings → Permissions, Sidebar menu).
    can: (code) => projectKey !== "" && session.can(projectKey, code),
    mayOpenDevOpsQueue: mayOpenDevOpsQueue(session),
    // Batch 17 (Emergency OTP E7): the Emergency bypasses report, for Chief Pilots and System Admins.
    mayOpenEmergencyReport: mayOpenEmergencyReport(session),
    // Batch 20 B20-1: Approvals for holders of an approval step on any project.
    mayOpenApprovals: mayOpenApprovals(session),
    // Batch 26: Status reports for anyone who may read a project's weekly status somewhere.
    mayOpenStatusReports: mayOpenStatusReports(session),
    // Settings → Menu (contract 0.10.50): which entries show and their order, system-wide; absent from an older server.
  }, session.me.menu);
  // Revision 94: "Display name (Real name)" once the person sets a display name (My profile), else the real name.
  const name = userLabel(session.user);

  return (
    <>
      <MentionDirectoryContext value={mentionDirectory}>
      <GuidesProvider projectKey={projectKey}>
      <a className="gf-skip" href="#main">
        Skip to content
      </a>
      <header className={phoneSearch.open ? "gf-header phone-search-open" : "gf-header"}>
        {phoneSearch.open && <PhoneSearchBar control={phoneSearch} onSearch={searchFor} />}
        <div className="gf-brand">
          <Link to="/">
            <img src={logoReverse} alt="Giraffe home" width={109} height={28} />
          </Link>
        </div>
        {phoneSearchOn ? (
          <div className="gf-phone-search">
            <PhoneSearchButton control={phoneSearch} />
          </div>
        ) : (
        <form className="gf-search" role="search" onSubmit={onSearch} data-guide="search">
          <label className="vh" htmlFor="gf-q">
            Search work items
          </label>
          <input
            ref={searchRef}
            id="gf-q"
            name="q"
            type="search"
            placeholder="Search, or type a key or global ID"
            autoComplete="off"
            key={location.pathname === "/search" ? "search" : "other"}
          />
          <kbd aria-hidden="true">/</kbd>
        </form>
        )}
        <div className="gf-actions">
          {showCreate && (
            <Link className="btn" to={createHref} data-guide="create">
              Create
            </Link>
          )}
          {session.isPartner && !phone && <span className="gf-partner-chip">Partner access</span>}
          {/* Batch 17 attention-bell merge §5 (ruling D1 (a)): the bell carries the Needs-attention number, so the header pill
              (and, for a partner, the Mentions link, ruling P6) shows only when the bell does not: flag off, or an AI agent. */}
          {bellOn && <NotificationBell />}
          {!bellOn &&
            (session.isPartner ? (
              mentionsOn && (
                <Link className="gf-attention" to="/mentions" aria-label={`Mentions: ${mentionsUnread} unread`} data-guide="attention">
                  Mentions
                  <span className="count" aria-hidden="true">
                    {attention.isSuccess ? mentionsUnread : "–"}
                  </span>
                </Link>
              )
            ) : (
              <Link
                className="gf-attention"
                to="/"
                data-guide="attention"
                aria-label={attention.isSuccess ? `Needs attention: ${count} ${count === 1 ? "item" : "items"}` : "Needs attention: count not available"}
              >
                Needs attention
                <span className="count" aria-hidden="true">
                  {attention.isSuccess ? count : "–"}
                </span>
              </Link>
            ))}
          {!phone && <FeedbackButton projectKey={projectKey} />}
          {!phone && <TestModeControl projectKey={projectKey} />}
          <Popover
            label="Account and what's on today"
            buttonClassName="gf-who"
            guide="user-menu"
            buttonLabel={`${phone && (session.testMode || feedbackOffer) ? `${name}, account menu and more` : `${name}, account menu`}${whatsNew.showDot ? ", what's new" : ""}`}
            button={
              <>
                <PersonAvatar className="gf-av" name={name} avatarUrl={session.user.avatarUrl} isAgent={session.user.isAgent === true} />
                <span className="nm">{name}</span>
                {whatsNew.showDot && <span className="gf-new-dot" aria-hidden="true" />}
              </>
            }
          >
            {(close) => (
              <div className="stack">
                <p>
                  <b>{name}</b>
                  <br />
                  <span className="help">{session.user.email}</span>
                </p>
                {phone && session.isPartner && <span className="gf-partner-chip gf-menu-chip">Partner access</span>}
                {phone && <TestModeControl projectKey={projectKey} />}
                {phone && feedbackOffer && (
                  <button
                    type="button"
                    className="btn sec"
                    data-guide="feedback"
                    title={FEEDBACK_TITLE}
                    onClick={() => {
                      close();
                      setPhoneFeedbackOpen(true);
                    }}
                  >
                    Feedback
                  </button>
                )}
                <Link className="btn sec" to="/me/profile" onClick={close}>
                  Profile
                </Link>
                {/* What's new (ruling of 17 Oct 2026): internal staff only; the dot shows until the newest release is seen. */}
                {!partnerAudience && (
                  <Link className="btn sec" to="/whats-new" onClick={close} data-guide="whats-new">
                    {"What's new"}
                    {whatsNew.showDot && <NewDot />}
                  </Link>
                )}
                <WhatsOnToday session={session} bellOn={bellOn} />
                {/* Batch 18 §10.7 (Features__NotificationSettings): one place to manage notices, so the menu links to it instead of
                    carrying the mention e-mail toggle. Flag off: SA-04 §7.11's opt-out stays here (the panel mounts only while open). */}
                {session.flag("NotificationSettings") ? (
                  <Link className="btn sec" to="/me/notifications/settings" onClick={close}>
                    Notification settings
                  </Link>
                ) : (
                  mentionsOn && <MentionEmailToggle />
                )}
                {/* Personal access tokens for MCP clients and scripts (Features__AccessTokens, Revision 95). */}
                {session.flag("AccessTokens") && (
                  <Link to="/me/access-tokens" className="btn sec" onClick={close}>
                    Access tokens
                  </Link>
                )}
                {/* Guided tours (Features__GuidedTours): replay any tour, or the welcome tour again. */}
                <GuidesMenu close={close} />
                {signOutError && (
                  <div className="msg" role="alert">
                    {signOutError}
                  </div>
                )}
                <button type="button" className="btn sec" onClick={() => void onSignOut()}>
                  Sign out
                </button>
              </div>
            )}
          </Popover>
        </div>
      </header>
      {phone && phoneFeedbackOpen && feedbackOffer && (
        <FeedbackLayer projectKey={projectKey} offer={feedbackOffer} onClose={() => setPhoneFeedbackOpen(false)} />
      )}
      <TestModeStrip projectKey={projectKey} />

      <div className="gf-shell">
        <nav className="gf-side" aria-label="Main" data-guide="sidebar">
          <div className="gf-project" data-guide="project-picker">
            <label id="gf-project-label" htmlFor="gf-project">
              Project
            </label>
            {/* Batch 18 GRF-47 (Features__ProjectIcons): a picker with each project's icon; flag off, the native select. */}
            {session.flag("ProjectIcons") ? (
              <ProjectPicker
                id="gf-project"
                projects={known}
                value={projectKey}
                onSelect={onProjectChange}
                loading={projects.isLoading}
                labelId="gf-project-label"
                describedBy={projects.isError ? "gf-project-help" : undefined}
              />
            ) : (
            <select
              id="gf-project"
              value={projectKey}
              onChange={(event) => onProjectChange(event.target.value)}
              disabled={known.length === 0}
              aria-describedby={projects.isError ? "gf-project-help" : undefined}
            >
              {known.length === 0 && <option value="">{projects.isLoading ? "Loading…" : "No projects"}</option>}
              {known.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.key} · {p.name}
                </option>
              ))}
            </select>
            )}
            {projects.isError && (
              <p id="gf-project-help" className="help">
                Projects could not be loaded. {problemMessage(projects.error)}
              </p>
            )}
            {noProjects && <p className="help">You have no projects yet.</p>}
          </div>
          {groups
            .map((group) => ({
              ...group,
              // What's new: not for an account whose every project role is Partner either (ruling of 17 Oct 2026).
              items: group.items.filter((screen) => (!screen.path.includes(":projectKey") || projectKey) && !(partnerAudience && screen.nav?.key === "whats-new")),
            }))
            .filter((group) => group.items.length > 0)
            .map((group) => (
              <div key={group.label} role="group" aria-labelledby={`nav-${group.label}`}>
                <div className="grp" id={`nav-${group.label}`}>
                  {group.label}
                </div>
                {group.items.map((screen) => (
                  <NavLink key={screen.path} to={screenHref(screen, projectKey)} end data-guide={screen.nav?.guide}>
                    {screen.nav?.label}
                    {screen.nav?.key === "whats-new" && whatsNew.showDot && <NewDot />}
                  </NavLink>
                ))}
              </div>
            ))}
        </nav>

        <main id="main" className="gf-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      </GuidesProvider>
      </MentionDirectoryContext>
    </>
  );
}
